'use client';
import React from 'react';
import { ArticleVideoPlayer } from '@benzinga/content-manager';
import { runningClientSide } from '@benzinga/utils';
import { NoFirstRender, useEffectDidMount } from '@benzinga/hooks';
import { useConnatixLivestream } from '@benzinga/livestream-hooks';

import { ImagePlacement } from './ImagePlacement';
import { ArticleData } from '@benzinga/article-manager';
import { useBenzingaEdge } from '@benzinga/edge';
import { isMobile } from '@benzinga/device-utils';

const ArticleImageGallery = React.lazy(() =>
  import('./ArticleImageGallery').then(module => {
    return { default: module.ArticleImageGallery };
  }),
);

const ConnatixVideoPlayer = React.lazy(() =>
  import('@benzinga/ads').then(module => {
    return { default: module.ConnatixVideoPlayer };
  }),
);

export interface ArticleMainPictureProps {
  articleData: ArticleData;
  imageOnly: boolean;
  primisId?: string;
  videoPlayerSettings?: ArticleVideoPlayer;
}

export const ArticleMainPicture: React.FC<ArticleMainPictureProps> = ({
  articleData,
  imageOnly,
  videoPlayerSettings,
}) => {
  const { assembleLivestream, livestreamData } = useConnatixLivestream();
  const [videoPlayerClass, setVideoPlayerClass] = React.useState('video-player-wrapper');
  const [showPlayer, setShowPlayer] = React.useState(false);

  const isBenzingaEdgeAccessLoading = useBenzingaEdge().isLoading;

  React.useEffect(() => {
    if (isBenzingaEdgeAccessLoading) return;
    if (!imageOnly) {
      assembleLivestream();
      if (livestreamData) {
        setVideoPlayerClass('video-player-wrapper livestream');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageOnly, isBenzingaEdgeAccessLoading, livestreamData]);

  useEffectDidMount(() => {
    if (isMobile()) {
      window.addEventListener(
        'touchstart',
        () => {
          setShowPlayer(true);
        },
        { once: true },
      );
    } else {
      window.addEventListener(
        'mousemove',
        () => {
          setShowPlayer(true);
        },
        { once: true },
      );
    }
  });

  const getPlayerComponent = React.useMemo(() => {
    if (!runningClientSide()) {
      return null;
    }

    const channels = articleData?.channels?.map(channel => channel.name);

    if (livestreamData) {
      return <ConnatixVideoPlayer {...livestreamData} channels={channels} />;
    }

    return <ConnatixVideoPlayer {...videoPlayerSettings?.connatix} channels={channels} />;
  }, [livestreamData, articleData?.channels, videoPlayerSettings?.connatix]);

  const slideshows = articleData?.meta?.wp_post_meta?.Slideshow;

  if (slideshows) {
    return <ArticleImageGallery slideshows={slideshows} />;
  }

  if ((imageOnly || !showPlayer) && articleData.primaryImage) {
    return (
      <ImagePlacement
        caption={articleData?.assets?.source}
        primaryImage={articleData.primaryImage}
        title={articleData?.title}
      />
    );
  }

  return (
    <div className={videoPlayerClass}>
      <div className="video-player-wrapper__inner">
        <NoFirstRender>{getPlayerComponent}</NoFirstRender>
      </div>
    </div>
  );
};
